/** @format */

import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space, Popconfirm, Spin, InputNumber } from 'antd';
import { DeleteOutlined, ReloadOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Campaign, CampaignFilterInput } from '../types';
import { GlobalVendorDropdown } from 'features/vendors';
import * as campaignService from '../services/campaignsService';
import { useUpdateCampaign } from '../hooks/useUpdateCampaign';
import { toast } from 'react-toastify';

interface CampaignTableProps {
  campaigns: Campaign[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onEditComplete?: () => Promise<void>;
  filter?: CampaignFilterInput;
}

interface EditingCampaign {
  id: string;
  fields: {
    vendorName?: string;
    globalId?: string;
    globalName?: string;
    cost?: number;
    buffer?: number;
  };
}

/**
 * Campaign Table Component
 * Displays campaigns with search, filter, sort, inline edit, and delete functionality
 */
export const CampaignTable: React.FC<CampaignTableProps> = ({
  campaigns,
  loading,
  onRefresh,
  onEditComplete,
  filter,
}) => {
  const [searchText, setSearchText] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingCampaign | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const { updateCampaign } = useUpdateCampaign();

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(
      (campaign) =>
        (campaign.vendorName?.toLowerCase() || '').includes(searchText.toLowerCase()) ||
        (campaign.queueId?.toString() || '').includes(searchText.toLowerCase()) ||
        (campaign.globalName?.toLowerCase() || '').includes(searchText.toLowerCase())
    );
  }, [campaigns, searchText]);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await campaignService.deleteCampaign(id);
      toast.success('Campaign deleted successfully');
      await onRefresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete campaign');
    } finally {
      setDeleting(null);
    }
  };

  const handleEditStart = (id: string, campaign: Campaign) => {
    setEditing({
      id,
      fields: {
        vendorName: campaign.vendorName,
        globalId: campaign.globalId,
        globalName: campaign.globalName,
        cost: campaign.cost,
        buffer: campaign.buffer,
      },
    });
  };

  const handleFieldChange = (field: string, value: any) => {
    if (!editing) return;
    setEditing((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: {
          ...prev.fields,
          [field]: value,
        },
      };
    });
  };

  const handleMultiFieldChange = (updates: Record<string, any>) => {
    if (!editing) return;
    setEditing((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fields: {
          ...prev.fields,
          ...updates,
        },
      };
    });
  };

  const handleEditSave = async (id: string) => {
    if (!editing || editing.id !== id) return;

    setUpdating(id);
    try {
      // Only update fields that have changed
      const updateData: any = {};
      if (editing.fields.vendorName !== undefined) updateData.vendorName = editing.fields.vendorName;
      if (editing.fields.globalId !== undefined) updateData.globalId = editing.fields.globalId;
      if (editing.fields.globalName !== undefined) updateData.globalName = editing.fields.globalName;
      if (editing.fields.cost !== undefined) updateData.cost = editing.fields.cost;
      if (editing.fields.buffer !== undefined) updateData.buffer = editing.fields.buffer;

      console.log('Updating campaign with data:', updateData);
      await updateCampaign(id, updateData);
      toast.success('Campaign updated successfully');
      await onRefresh(); // Refresh to get the latest data from backend
      setEditing(null);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update campaign');
    } finally {
      setUpdating(null);
    }
  };

  const handleEditCancel = () => {
    setEditing(null);
  };

  const columns = [
    {
      title: 'Queue ID',
      dataIndex: 'queueId',
      key: 'queueId',
      width: 120,
      sorter: (a: Campaign, b: Campaign) => (a.queueId || 0) - (b.queueId || 0),
    },
    {
      title: 'Vendor Name',
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 180,
      sorter: (a: Campaign, b: Campaign) => (a.vendorName || '').localeCompare(b.vendorName || ''),
      render: (vendorName: string | undefined, record: Campaign) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <Input
            value={editing.fields.vendorName}
            onChange={(e) => handleFieldChange('vendorName', e.target.value)}
            placeholder='Enter vendor name'
            autoFocus
            style={{ width: '100%' }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {vendorName || '-'}
          </span>
        );
      },
    },
    {
      title: 'Global Name',
      dataIndex: 'globalName',
      key: 'globalName',
      width: 150,
      sorter: (a: Campaign, b: Campaign) =>
        (a.globalName || '').localeCompare(b.globalName || ''),
      render: (globalName: string | undefined, record: Campaign) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <GlobalVendorDropdown
            value={editing.fields.globalId}
            onChange={(globalId, vendor) => {
              handleMultiFieldChange({
                globalId,
                globalName: vendor?.globalName,
              });
            }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {globalName || '-'}
          </span>
        );
      },
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: 130,
      sorter: (a: Campaign, b: Campaign) => (a.cost || 0) - (b.cost || 0),
      render: (cost: number | undefined, record: Campaign) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <InputNumber
            value={editing.fields.cost}
            onChange={(val) => handleFieldChange('cost', val)}
            min={0}
            style={{ width: '100%' }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {cost ? `$${cost}` : '-'}
          </span>
        );
      },
    },
    {
      title: 'Buffer %',
      dataIndex: 'buffer',
      key: 'buffer',
      width: 130,
      sorter: (a: Campaign, b: Campaign) => (a.buffer || 0) - (b.buffer || 0),
      render: (buffer: number | undefined, record: Campaign) => {
        const isEditing = editing?.id === record._id;
        return isEditing ? (
          <InputNumber
            value={editing.fields.buffer}
            onChange={(val) => handleFieldChange('buffer', val)}
            min={0}
            max={100}
            style={{ width: '100%' }}
          />
        ) : (
          <span onDoubleClick={() => handleEditStart(record._id, record)}>
            {buffer ? `${buffer}%` : '0%'}
          </span>
        );
      },
    },
    
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: any, record: Campaign) => {
        const isEditing = editing?.id === record._id;
        return (
          <Space size='small'>
            {isEditing ? (
              <>
                <Button
                  type='text'
                  size='small'
                  icon={<CheckOutlined />}
                  onClick={() => handleEditSave(record._id)}
                  loading={updating === record._id}
                />
                <Button
                  type='text'
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={handleEditCancel}
                  danger
                />
              </>
            ) : (
              <Button
                type='text'
                size='small'
                icon={<EditOutlined />}
                onClick={() => handleEditStart(record._id, record)}
                title='Click to edit all fields, or double-click any field'
              />
            )}
            <Popconfirm
              title='Delete Campaign'
              description='Are you sure you want to delete this campaign?'
              onConfirm={() => handleDelete(record._id)}
              okText='Yes'
              cancelText='No'
              disabled={deleting === record._id}
            >
              <Button
                type='text'
                danger
                size='small'
                icon={<DeleteOutlined />}
                loading={deleting === record._id}
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input
          placeholder='Search by Queue ID, Vendor Name, or Global Name...'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: '400px' }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={loading}
          type='primary'
        >
          Refresh
        </Button>
      </div>

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredCampaigns}
          rowKey='_id'
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (total) => `Total ${total} campaigns`,
          }}
          size='small'
        />
      </Spin>
    </div>
  );
};

export default CampaignTable;
